import {
  OnboardingFormDraft,
  OnboardingProgressModuleKey,
  OnboardingVesselContext,
} from '../service/onboarding-progress/onboarding-progress.service';

/** Map UI module keys to backend onboarding_modules values. */
export function mapOnboardingModuleKeys(modules: string[]): string[] {
  return [
    ...new Set(
      modules.map((module) => (module === 'speed' ? 'sailing' : module)),
    ),
  ];
}

export function buildModuleOnboardingSubmitPayload(
  completingModule: OnboardingProgressModuleKey,
  options: {
    draft?: OnboardingFormDraft | null;
    vesselContext?: Partial<OnboardingVesselContext>;
    moduleData?: Record<string, unknown> | null;
    onboardingStatus?: string;
  } = {},
): Record<string, unknown> {
  const draft = options.draft ?? null;
  const formValue = (draft?.formValue ?? {}) as Record<string, unknown>;
  const selectedModules = draft?.selectedModules?.length
    ? [...draft.selectedModules]
    : [completingModule];

  if (!selectedModules.includes(completingModule)) {
    selectedModules.push(completingModule);
  }

  const context = options.vesselContext ?? {};
  const normalizedImo = String(context.imo ?? formValue['imoNumber'] ?? '')
    .replace(/\D/g, '')
    .trim();

  const payload: Record<string, unknown> = {
    submitted_at: new Date().toISOString(),
    onboarding_modules: mapOnboardingModuleKeys(selectedModules),
    onboarding_status: options.onboardingStatus ?? 'completed',
    completing_module: completingModule,
    vessel: {
      vessel_name: String(context.vessel_name ?? formValue['ship'] ?? '').trim(),
      imo: normalizedImo,
      vessel_type: String(context.vessel_type ?? formValue['typeOfShip'] ?? ''),
      segment: String(context.segment ?? formValue['segment'] ?? ''),
      client_id: Number(context.client_id ?? formValue['client'] ?? 0),
    },
    onboarding_form: {
      ...formValue,
      imoNumber: normalizedImo,
    },
    quick_onboarding: formValue['quickOnboarding'] === true,
  };

  if (completingModule === 'emission' && options.moduleData) {
    payload['emission'] = options.moduleData;
  }

  if (completingModule === 'cargo' && options.moduleData) {
    payload['cargo_heating'] = options.moduleData;
  }

  if (completingModule === 'port') {
    payload['port_consumption'] = options.moduleData ?? { status: 'completed' };
  }

  return payload;
}

export function normalizeOnboardingSubmitPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...payload };

  if (Array.isArray(normalized['onboarding_modules'])) {
    normalized['onboarding_modules'] = mapOnboardingModuleKeys(
      normalized['onboarding_modules'] as string[],
    );
  }

  if (!normalized['submitted_at']) {
    normalized['submitted_at'] = new Date().toISOString();
  }

  if (!normalized['onboarding_status']) {
    normalized['onboarding_status'] = 'completed';
  }

  if (normalized['emission'] == null) {
    delete normalized['emission'];
  }

  delete normalized['completing_module'];

  return normalized;
}

export function buildSlimOnboardingFormValue(
  formValue: Record<string, unknown>,
  normalizedImo: string,
  clientName = '',
): Record<string, unknown> {
  return {
    quickOnboarding: formValue['quickOnboarding'] === true,
    ship: String(formValue['ship'] ?? '').trim(),
    imoNumber: normalizedImo,
    typeOfShip: formValue['typeOfShip'] ?? '',
    segment: formValue['segment'] ?? '',
    client: Number(formValue['client'] ?? 0),
    clientName: String(formValue['clientName'] ?? clientName ?? '').trim(),
    merakiPic: formValue['merakiPic'] ?? '',
    status: formValue['status'] ?? '',
    startDate: formValue['startDate'] ?? '',
    endDate: formValue['endDate'] ?? '',
  };
}

export function serializeOnboardingFormValue(
  formValue: Record<string, unknown>,
  normalizedImo: string,
): Record<string, unknown> {
  try {
    return JSON.parse(
      JSON.stringify({
        ...formValue,
        imoNumber: normalizedImo,
      }),
    ) as Record<string, unknown>;
  } catch {
    return buildSlimOnboardingFormValue(formValue, normalizedImo);
  }
}

export function sanitizeApiErrorText(text: string): string {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) {
    return '';
  }

  if (/<!doctype\s+html|<html[\s>]/i.test(trimmed)) {
    if (/500\s+Internal Server Error/i.test(trimmed)) {
      return 'Server error (500). Please try again or contact support.';
    }
    if (/404\s+Not Found/i.test(trimmed)) {
      return 'The requested API was not found (404).';
    }
    if (/401\s+Unauthorized/i.test(trimmed)) {
      return 'You are not authorized to perform this action.';
    }
    if (/403\s+Forbidden/i.test(trimmed)) {
      return 'You do not have permission to perform this action.';
    }
    return 'An unexpected server error occurred. Please try again.';
  }

  if (trimmed.length > 280) {
    return `${trimmed.slice(0, 280)}…`;
  }

  return trimmed;
}

export function isOnboardingSubmitLegacySuccess(err: unknown): boolean {
  const errorBody = (err as { error?: Record<string, unknown> })?.error;
  return errorBody?.['text'] === 'save_button_success';
}

export function extractOnboardingSubmitSuccessMessage(
  response: unknown,
  fallback = 'Onboarding submitted successfully',
): string {
  if (!response) {
    return fallback;
  }

  const body = response as Record<string, unknown>;
  if (body['text'] === 'save_button_success') {
    return fallback;
  }

  return String(body['message'] || body['msg'] || fallback);
}

export function extractOnboardingSubmitErrorMessage(
  err: unknown,
  fallback = 'Onboarding submission failed',
): string {
  const httpErr = err as {
    status?: number;
    error?: Record<string, unknown> | string;
    message?: string;
  };
  const errorBody = httpErr?.error;
  let message = '';

  if (typeof errorBody === 'string') {
    message = sanitizeApiErrorText(errorBody);
  } else if (errorBody && typeof errorBody === 'object') {
    const candidate = String(
      errorBody['error'] ||
        errorBody['message'] ||
        errorBody['details'] ||
        errorBody['detail'] ||
        errorBody['text'] ||
        '',
    ).trim();
    message = sanitizeApiErrorText(candidate);
  }

  if (!message && httpErr?.message) {
    message = sanitizeApiErrorText(httpErr.message);
  }

  if (!message && httpErr?.status) {
    if (httpErr.status >= 500) {
      return 'Server error. Please try again later.';
    }
    if (httpErr.status === 404) {
      return 'Onboarding API not found.';
    }
    if (httpErr.status === 401 || httpErr.status === 403) {
      return 'You are not authorized to submit onboarding.';
    }
    if (httpErr.status === 400) {
      return 'Invalid onboarding data. Please review the form and try again.';
    }
  }

  return message || fallback;
}
